/**
 * Caption Showcase Timeline
 * 
 * Remotion-compatible timeline version of the caption showcase.
 * Creates a sequence showing different animations and fonts automatically
 * without interactive controls - perfect for Remotion Studio preview.
 */

import React from 'react';
import { useCurrentFrame } from 'remotion';
import { AdvancedTextEngine } from './AdvancedTextEngine';
import { 
    AdvancedTextConfig, 
    TextPageData, 
    TextToken 
} from './types';
import { FontLoader } from './FontUtils';


// Demo configurations cycling through different effects
const DEMO_SEQUENCE = [
    {
        name: 'Bangers Highlight',
        text: 'BANGERS FONT HIGHLIGHT ANIMATION',
        fontFamily: 'Bangers',
        animation: 'highlight' as const,
        primaryColor: '#5dade2', // Softer blue (for outline)
        highlightColor: '#ff6b35', // Orange (for highlight)
        outline: 'stroke' as const,
        background: 'translucent' as const,
        duration: 8
    },
    {
        name: 'Titan One Pop',
        text: 'TITAN ONE POP PHYSICS ANIMATION',
        fontFamily: 'Titan One',
        animation: 'pop' as const,
        primaryColor: '#4ecdc4', // Teal
        highlightColor: '#8e44ad', // Purple
        outline: '3d-stroke' as const,
        background: 'none' as const,
        duration: 6
    },
    {
        name: 'Playfair Karaoke',
        text: 'Playfair Display Karaoke Style',
        fontFamily: 'Playfair Display',
        animation: 'karaoke' as const,
        primaryColor: '#2ecc71', // Green
        highlightColor: '#ff4757', // Red
        outline: 'glow' as const,
        background: 'colored' as const,
        duration: 8
    },
    {
        name: 'Black Han Boxed',
        text: 'BLACK HAN SANS BOXED EFFECT',
        fontFamily: 'Black Han Sans',
        animation: 'boxed' as const,
        primaryColor: '#34495e', // Dark Blue-Gray
        highlightColor: '#f39c12', // Yellow-Orange
        outline: 'none' as const,
        background: 'none' as const,
        duration: 7
    },
    {
        name: 'Rubik Scale',
        text: 'RUBIK WET PAINT SCALE EFFECT',
        fontFamily: 'Rubik Wet Paint',
        animation: 'scale' as const,
        primaryColor: '#ff9800', // Orange
        highlightColor: '#9b59b6', // Purple
        outline: '3d-stroke' as const,
        background: 'none' as const,
        duration: 6
    },
    {
        name: 'Lora Slide Up',
        text: 'Lora Slide Up Smooth Motion',
        fontFamily: 'Lora',
        animation: 'slide_up' as const,
        primaryColor: '#2c3e50', // Dark Blue
        highlightColor: '#e67e22', // Orange
        outline: 'shadow' as const,
        background: 'white' as const,
        duration: 6
    },
    {
        name: 'Roboto Slide Left',
        text: 'ROBOTO SLIDE LEFT DYNAMIC ENTRY',
        fontFamily: 'Roboto Condensed',
        animation: 'slide_left' as const,
        primaryColor: '#27ae60', // Green
        highlightColor: '#e74c3c', // Red
        outline: 'glow' as const,
        background: 'translucent' as const,
        duration: 6
    }
];

export const CaptionShowcaseTimeline: React.FC = () => {
    const frame = useCurrentFrame();
    const fps = 30;
    
    // Calculate which demo should be playing
    let currentDemo = DEMO_SEQUENCE[0];
    let demoStartFrame = 0;
    let demoFrame = frame;
    
    let accumulatedFrames = 0;
    for (const demo of DEMO_SEQUENCE) {
        const demoFrames = demo.duration * fps;
        if (frame >= accumulatedFrames && frame < accumulatedFrames + demoFrames) {
            currentDemo = demo;
            demoStartFrame = accumulatedFrames;
            demoFrame = frame - accumulatedFrames;
            break;
        }
        accumulatedFrames += demoFrames;
    }
    
    // Create tokens for the current demo
    const words = currentDemo.text.split(' ');
    const totalDuration = currentDemo.duration;
    // FIXED: Make word timing 3x faster (divide word duration by 3)
    const wordDuration = (totalDuration * 1000) / words.length / 3;
    
    const tokens: TextToken[] = words.map((word, index) => ({
        text: word,
        fromMs: index * wordDuration,
        toMs: (index + 1) * wordDuration,
        index: index
    }));

    // Create page data
    const pageData: TextPageData = {
        text: currentDemo.text,
        tokens: tokens,
        startMs: 0
    };

    // Calculate dynamic background based on current demo
    const getBackgroundStyle = () => {
        const baseStyle = {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
        };

        // Cycle through different background gradients for visual variety
        const backgrounds = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blue-purple
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink-red
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Blue-cyan
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green-cyan
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Pink-yellow
            'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', // Purple-pink (for slide_up)
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'  // Orange-peach (for slide_left)
        ];
        
        const backgroundIndex = Math.floor(demoStartFrame / (8 * fps)) % backgrounds.length;
        
        return {
            ...baseStyle,
            background: backgrounds[backgroundIndex]
        };
    };


    // Create configuration
    const config: AdvancedTextConfig = {
        primaryColor: currentDemo.primaryColor,
        currentSpokenWordColor: currentDemo.highlightColor,
        fontFamily: currentDemo.fontFamily,
        maxFontSize: 84, // Extra large for visibility
        outline: currentDemo.outline,
        background: currentDemo.background,
        textAnimation: currentDemo.animation,
        borderRadius: 0.2,
        captionCase: currentDemo.animation === 'boxed' || 
                    currentDemo.fontFamily === 'Bangers' ||
                    currentDemo.fontFamily === 'Titan One' ||
                    currentDemo.fontFamily === 'Black Han Sans' ||
                    currentDemo.fontFamily === 'Rubik Wet Paint'
                    ? 'uppercase' : 'standard',
        hidePunctuation: false,
        containerPadding: { x: 0.15, y: 0.1 },
        boxPadding: { x: 0.12, y: 0.08 }
    };

    // Create demo title overlay
    const demoTitle = (
        <div style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            background: 'rgba(0,0,0,0.7)',
            padding: '10px 20px',
            borderRadius: '10px',
            fontFamily: 'system-ui, sans-serif',
            textAlign: 'center',
            zIndex: 10
        }}>
            {currentDemo.name} - {currentDemo.fontFamily}
        </div>
    );

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {/* Dynamic background */}
            <div style={getBackgroundStyle()} />
            
            {/* Demo title */}
            {demoTitle}
            
            {/* Main caption demo */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <FontLoader 
                    fonts={[currentDemo.fontFamily]} 
                    initializeClipCreatorFonts={true}
                >
                    <AdvancedTextEngine
                        page={pageData}
                        config={config}
                        videoConfig={{ fps: fps, width: 1920 }}
                        currentFrame={demoFrame}
                        position="center"
                    />
                </FontLoader>
            </div>
            
            {/* Progress indicator */}
            <div style={{
                position: 'absolute',
                bottom: '30px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px'
            }}>
                {DEMO_SEQUENCE.map((demo, index) => {
                    let isActive = false;
                    let frameCount = 0;
                    
                    for (let i = 0; i <= index; i++) {
                        const prevFrameCount = frameCount;
                        frameCount += DEMO_SEQUENCE[i].duration * fps;
                        if (frame >= prevFrameCount && frame < frameCount) {
                            isActive = i === index;
                            break;
                        }
                    }
                    
                    return (
                        <div
                            key={index}
                            style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: isActive ? '#ffffff' : 'rgba(255,255,255,0.3)',
                                transition: 'all 0.3s ease'
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default CaptionShowcaseTimeline;